// Simple localStorage TTL cache helper
// - getCache: synchronous read, returns null if expired or unavailable
// - setCache: write with TTL
// - fetchAndCache: convenience to fetch then cache
// - clearCache / clearAllCache

const PREFIX = 'my_site_cache_v1:';

const supportsLocalStorage = typeof window !== 'undefined' && !!window.localStorage;

interface CacheEntry<T> {
    value: T;
    expiresAt: number;
}

function keyFor(k: string) {
    return PREFIX + k;
}

export function setCache<T>(k: string, value: T, ttlMs: number) {
    if (!supportsLocalStorage) return;
    try {
        const entry: CacheEntry<T> = { value, expiresAt: Date.now() + ttlMs };
        localStorage.setItem(keyFor(k), JSON.stringify(entry));
    } catch (e) {
        // ignore storage errors
    }
}

export function getCache<T>(k: string): T | null {
    if (!supportsLocalStorage) return null;
    try {
        const raw = localStorage.getItem(keyFor(k));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CacheEntry<T>;
        if (!parsed || typeof parsed.expiresAt !== 'number') {
            localStorage.removeItem(keyFor(k));
            return null;
        }
        if (Date.now() > parsed.expiresAt) {
            localStorage.removeItem(keyFor(k));
            return null;
        }
        return parsed.value;
    } catch (e) {
        return null;
    }
}

export async function fetchAndCache<T>(k: string, loader: () => Promise<T>, ttlMs: number): Promise<T> {
    const data = await loader();
    setCache(k, data, ttlMs);
    return data;
}

export function clearCache(k: string) {
    if (!supportsLocalStorage) return;
    try { localStorage.removeItem(keyFor(k)); } catch { }
}

export function clearAllCache() {
    if (!supportsLocalStorage) return;
    try {
        Object.keys(localStorage).forEach(k => {
            if (k.startsWith(PREFIX)) localStorage.removeItem(k);
        });
    } catch (e) {
        // ignore
    }
}
